import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { PageHeader, Section } from '@/components/PageHeader.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Input, FormField } from '@/components/ui/Input.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/Dialog.jsx';
import { Badge } from '@/components/ui/Badge.jsx';
import { ConfirmDialog } from '@/components/ConfirmDialog.jsx';
import { DataTable } from '@/components/DataTable.jsx';
import { useToast } from '@/components/ui/Toast.jsx';
import { periodLabel, formatDateTime } from '@/lib/format.js';
import { Plus, Eye, Trash2 } from 'lucide-react';

const STATUS_VARIANT = { draft: 'warning', finalized: 'success', paid: 'brand', reopened: 'info' };

export default function PayrollRunsPage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const now = new Date();
  const [draft, setDraft] = useState({ period_year: now.getFullYear(), period_month: now.getMonth() + 1 });
  const [deleteId, setDeleteId] = useState(null);

  const { data = [], isLoading } = useQuery({ queryKey: ['payrollRuns'], queryFn: () => api.get('/payroll/runs') });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.del(`/payroll/runs/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['payrollRuns'] }); toast({ variant: 'success', title: 'Draft deleted' }); },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });

  const create = useMutation({
    mutationFn: () => api.post('/payroll/runs', { period_year: Number(draft.period_year), period_month: Number(draft.period_month) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['payrollRuns'] }); setOpen(false); toast({ variant: 'success', title: 'Draft computed' }); },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });

  const columns = [
    { key: 'period', header: 'Period', sortable: true, accessor: (r) => `${r.period_year}-${String(r.period_month).padStart(2, '0')}`,
      render: (r) => <Link className="text-brand-600 hover:underline" to={`/payroll/${r.id}`}>{periodLabel(r.period_year, r.period_month)}</Link> },
    { key: 'status', header: 'Status', render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'default'}>{r.status}</Badge> },
    { key: 'computed_at', header: 'Computed', render: (r) => r.computed_at ? formatDateTime(r.computed_at) : '—', className: 'font-mono' },
    { key: 'finalized_at', header: 'Finalized', render: (r) => r.finalized_at ? formatDateTime(r.finalized_at) : '—', className: 'font-mono' },
    { key: 'paid_at', header: 'Paid', render: (r) => r.paid_at ? formatDateTime(r.paid_at) : '—', className: 'font-mono' },
    { key: '__a', header: '', className: 'w-28 text-right',
      render: (r) => (
        <div className="flex justify-end gap-1">
          {r.status === 'draft' && (
            <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700"
              onClick={() => setDeleteId(r.id)}
              disabled={deleteMutation.isPending}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button asChild variant="ghost" size="icon"><Link to={`/payroll/${r.id}`}><Eye className="h-4 w-4" /></Link></Button>
        </div>
      ) },
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Payroll Runs" description="Draft, finalize and pay monthly payroll"
        actions={<Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" />New Run</Button>}
      />
      <Section><DataTable columns={columns} data={data} loading={isLoading} exportFilename="payroll-runs.csv" /></Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Compute Payroll Draft</DialogTitle></DialogHeader>
          {(() => {
            const conflict = data.find(
              (r) => r.period_year === Number(draft.period_year) &&
                     r.period_month === Number(draft.period_month) &&
                     (r.status === 'finalized' || r.status === 'paid'),
            );
            return (
              <div className="space-y-3">
                <p className="text-sm text-slate-500">A draft run can be recomputed and edited until finalized. Re-running for an existing month replaces the draft.</p>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Year"><Input type="number" value={draft.period_year} onChange={(e) => setDraft({ ...draft, period_year: e.target.value })} /></FormField>
                  <FormField label="Month"><Input type="number" min="1" max="12" value={draft.period_month} onChange={(e) => setDraft({ ...draft, period_month: e.target.value })} /></FormField>
                </div>
                {conflict && (
                  <p className="text-sm text-red-600 font-medium">
                    A <span className="capitalize">{conflict.status}</span> payroll run already exists for this period. Reopen it to modify.
                  </p>
                )}
                <DialogFooter>
                  <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                  <Button onClick={() => create.mutate()} disabled={create.isPending || !!conflict}>
                    {create.isPending ? 'Computing…' : 'Compute'}
                  </Button>
                </DialogFooter>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteId != null}
        onOpenChange={(o) => !o && setDeleteId(null)}
        title="Delete this draft run?"
        description="This action cannot be undone."
        confirmText="Delete"
        onConfirm={() => deleteMutation.mutate(deleteId)}
      />
    </div>
  );
}
